import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { api, formatApiError } from '@/lib/api';
import PasswordInput from '@/components/PasswordInput';

const EMAIL_PREF_LABELS = [
  { key: 'new_application', labelKey: 'profile.pref_new_application' },
  { key: 'selected_as_receiver', labelKey: 'profile.pref_selected_as_receiver' },
  { key: 'application_withdrawn', labelKey: 'profile.pref_application_withdrawn' },
  { key: 'unrehomed', labelKey: 'profile.pref_unrehomed' },
];

export default function Profiel() {
  const { user, refresh, setUser } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isDonateur = user.role === 'donateur';

  const [form, setForm] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    username: user.username || '',
    email: user.email || '',
    phone: user.phone || '',
    password: '',
    preferredLanguage: user.preferredLanguage || 'nl',
    donorType: user.donorType || 'particulier',
    companyName: user.companyName || '',
  });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [prefs, setPrefs] = useState(null);
  const [prefMsg, setPrefMsg] = useState('');

  const [reportYear, setReportYear] = useState('');
  const [reportYears, setReportYears] = useState([]);
  const [reportBusy, setReportBusy] = useState(false);

  useEffect(() => {
    api.get('/users/me/email-preferences')
      .then(({ data }) => setPrefs(data))
      .catch(() => setPrefs({}));

    const reportEndpoint = isDonateur
      ? '/donateur/me/stats/available-years'
      : (user.organisationId ? '/organisations/me/stats/available-years' : null);

    if (reportEndpoint) {
      api.get(reportEndpoint)
        .then(({ data }) => {
          const currentYear = String(new Date().getFullYear());
          const years = (data?.years && data.years.length > 0)
            ? data.years
            : [currentYear];
          if (!years.includes(currentYear)) years.unshift(currentYear);
          setReportYears(years);
          setReportYear(years[0]);
        })
        .catch(() => {
          const fallback = String(new Date().getFullYear());
          setReportYears([fallback]);
          setReportYear(fallback);
        });
    }
  }, [isDonateur, user.organisationId]);

  const downloadReport = async () => {
    setReportBusy(true);
    try {
      const lang = 'nl';
      const endpoint = isDonateur ? '/donateur/me/stats/report' : '/organisations/me/stats/report';
      const response = await api.get(endpoint, {
        params: { year: reportYear, lang },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `inlimbo-verslag-${reportYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(t('profile.report_download_failed'));
    } finally {
      setReportBusy(false);
    }
  };

  const togglePref = async (key) => {
    if (!prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setPrefMsg('');
    try {
      const { data } = await api.patch('/users/me/email-preferences', { [key]: next[key] });
      setPrefs(data);
      setPrefMsg(t('profile.pref_saved'));
      setTimeout(() => setPrefMsg(''), 1500);
    } catch (e) {
      setPrefs(prefs); // revert
      alert(formatApiError(e));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(''); setErr('');
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (isDonateur) {
        delete payload.lastName;
        delete payload.phone;
      } else {
        delete payload.username;
        delete payload.donorType;
        delete payload.companyName;
      }
      await api.patch('/users/me', payload);
      await refresh();
      if (payload.preferredLanguage) {
        i18n.changeLanguage(payload.preferredLanguage);
      }
      setMsg(t('profile.update_success'));
      setForm((f) => ({ ...f, password: '' }));
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await api.delete('/users/me');
      setUser(false);
      navigate('/');
    } catch (e) {
      setDeleteError(formatApiError(e));
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-16" data-testid="profiel-page">
      <p className="overline mb-3">{t('nav.profile')}</p>
      <h1 className="text-4xl font-bold tracking-tight mb-10">{t('profile.title')}</h1>

      <form onSubmit={save} className="space-y-5">
        {isDonateur ? (
          <>
            <div>
              <label className="label-overline" htmlFor="profiel-username">{t('profile.username_label')}</label>
              <input
                id="profiel-username"
                className="input-flat"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                data-testid="profiel-username"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('profile.username_public_hint')}
              </p>
            </div>
            <div>
              <label className="label-overline" htmlFor="profiel-firstname">{t('auth.donateur_first_name_label')}</label>
              <input
                id="profiel-firstname"
                className="input-flat"
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                data-testid="profiel-firstname"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('profile.donateur_first_name_hint')}
              </p>
            </div>
            <div>
              <label className="label-overline">{t('auth.donateur_type_question')}</label>
              <div className="flex gap-4 mt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="donorType"
                    value="particulier"
                    checked={form.donorType === 'particulier'}
                    onChange={() => setForm({ ...form, donorType: 'particulier' })}
                    data-testid="profiel-donortype-particulier"
                  />
                  <span className="text-sm">{t('auth.donateur_type_particulier')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="donorType"
                    value="bedrijf"
                    checked={form.donorType === 'bedrijf'}
                    onChange={() => setForm({ ...form, donorType: 'bedrijf' })}
                    data-testid="profiel-donortype-bedrijf"
                  />
                  <span className="text-sm">{t('auth.donateur_type_bedrijf')}</span>
                </label>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {t('profile.donateur_donor_type_hint')}
              </p>
            </div>
            {form.donorType === 'bedrijf' && (
              <div>
                <label className="label-overline" htmlFor="profiel-companyname">{t('auth.donateur_company_name_label')}</label>
                <input
                  id="profiel-companyname"
                  className="input-flat"
                  value={form.companyName}
                  onChange={(e) => setForm({ ...form, companyName: e.target.value })}
                  data-testid="profiel-companyname"
                />
              </div>
            )}
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label-overline" htmlFor="profiel-firstname">{t('profile.first_name_label')}</label>
                <input id="profiel-firstname" className="input-flat" value={form.firstName} onChange={(e) => setForm({...form, firstName: e.target.value})} data-testid="profiel-firstname" />
              </div>
              <div>
                <label className="label-overline" htmlFor="profiel-lastname">{t('profile.last_name_label')}</label>
                <input id="profiel-lastname" className="input-flat" value={form.lastName} onChange={(e) => setForm({...form, lastName: e.target.value})} data-testid="profiel-lastname" />
              </div>
            </div>
            <div>
              <label className="label-overline" htmlFor="profiel-phone">{t('profile.phone_label')}</label>
              <input id="profiel-phone" className="input-flat" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="profiel-phone" />
            </div>
          </>
        )}
        <div>
          <label className="label-overline" htmlFor="profiel-email">{t('profile.email_label')}</label>
          <input id="profiel-email" type="email" className="input-flat" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="profiel-email" />
        </div>
        <div>
          <label className="label-overline" htmlFor="profiel-language">{t('profile.language_label')}</label>
          <select
            id="profiel-language"
            className="input-flat"
            value={form.preferredLanguage}
            onChange={(e) => setForm({ ...form, preferredLanguage: e.target.value })}
            data-testid="profiel-language"
          >
            <option value="nl">Nederlands</option>
            <option value="fr">Français</option>
          </select>
        </div>
        <div>
          <label className="label-overline" htmlFor="profiel-password">{t('profile.new_password')}</label>
          <PasswordInput id="profiel-password" className="input-flat" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} data-testid="profiel-password" />
        </div>

        {msg && <p className="text-sm bg-green-50 border border-green-300 text-green-900 px-3 py-2" data-testid="profiel-success">{msg}</p>}
        {err && <p className="text-sm text-destructive bg-destructive/10 border border-destructive/40 px-3 py-2" data-testid="profiel-error">{err}</p>}

        <button type="submit" disabled={saving} className="btn-primary" data-testid="profiel-submit">
          {saving ? t('profile.saving') : t('profile.save_btn')}
        </button>
      </form>

      {!isDonateur && (
        <div className="mt-16 border-t border-border pt-6" data-testid="profiel-organisatie-section">
          <p className="overline mb-2">{t('profile.organisation_section')}</p>
          <p className="text-sm text-muted-foreground mb-3">
            {t('profile.organisation_subtitle')}
          </p>
          <Link to="/organisatie" className="btn-primary inline-block" data-testid="profiel-organisatie-link">
            {t('profile.my_organisation_link')}
          </Link>
        </div>
      )}

      {(isDonateur || user.organisationId) && (
        <div className="mt-16 border-t border-border pt-6" data-testid="profiel-report-section">
          <p className="overline mb-1">{t('profile.report_title')}</p>
          <p className="text-sm text-muted-foreground mb-4">
            {isDonateur ? t('profile.report_subtitle_donateur') : t('profile.report_subtitle')}
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={reportYear}
              onChange={(e) => setReportYear(e.target.value)}
              className="input-flat"
              data-testid="profiel-report-year-select"
            >
              {reportYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              onClick={downloadReport}
              disabled={reportBusy || !reportYear}
              className="btn-secondary"
              data-testid="profiel-report-download-btn"
            >
              {reportBusy ? t('profile.report_downloading') : t('profile.report_download_btn')}
            </button>
          </div>
        </div>
      )}

      {/*
      <div className="mt-16 border-t border-border pt-6 text-sm">
        <p className="overline mb-2">Account</p>
        <p className="text-muted-foreground">Rol: {user.role} · Status: {user.status}</p>
      </div>*/}

      <div className="mt-16 border-t border-border pt-6" data-testid="profiel-email-prefs">
        <div className="flex items-end justify-between gap-3 mb-4">
          <div>
            <p className="overline mb-2">{t('profile.email_prefs')}</p>
            <p className="text-sm text-muted-foreground">
              {t('profile.email_prefs_subtitle')}
            </p>
          </div>
          {prefMsg && (
            <span className="text-xs text-[#34D399] font-medium" data-testid="email-prefs-saved">
              {prefMsg}
            </span>
          )}
        </div>
        <ul className="divide-y divide-border border-y border-border">
          {EMAIL_PREF_LABELS.map((p) => (
            <li key={p.key} className="py-3 flex items-center justify-between gap-4">
              <span className="text-sm">{t(p.labelKey)}</span>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={prefs ? !!prefs[p.key] : true}
                  onChange={() => togglePref(p.key)}
                  disabled={!prefs}
                  data-testid={`email-pref-${p.key}`}
                  className="h-5 w-5 accent-[#34D399] cursor-pointer"
                />
              </label>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-16 border-t border-border pt-6" data-testid="profiel-delete-account">
        <p className="overline mb-2 text-destructive">{t('profile.delete_account_title')}</p>
        <p className="text-sm text-muted-foreground mb-4">
          {t('profile.delete_account_body')}
        </p>

        {deleteError && (
          <p className="text-destructive text-sm mb-3" data-testid="profiel-delete-error">{deleteError}</p>
        )}

        {!confirmingDelete ? (
          <button
            type="button"
            onClick={() => setConfirmingDelete(true)}
            className="text-sm text-destructive underline underline-offset-4 hover:opacity-70"
            data-testid="profiel-delete-account-btn"
          >
            {t('profile.delete_account_cta')}
          </button>
        ) : (
          <div className="border border-destructive/40 bg-destructive/5 p-4" data-testid="profiel-delete-confirm">
            <p className="text-sm font-medium mb-4">{t('profile.delete_account_confirm')}</p>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="btn-secondary !border-destructive !text-destructive hover:!bg-destructive hover:!text-background text-sm"
                data-testid="profiel-delete-account-confirm-btn"
              >
                {deleting ? t('common.loading') : t('profile.delete_account_confirm_btn')}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                disabled={deleting}
                className="text-sm text-muted-foreground hover:text-foreground"
                data-testid="profiel-delete-account-cancel-btn"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
